import type { ApolloQueryResult, FetchPolicy } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useDebouncedCallback } from "@fluidattacks/design";
import { useCallback } from "react";

import { GET_TOE_INPUTS } from "../queries";
import type {
  GetToeInputsQuery,
  GetToeInputsQueryVariables,
  ToeInputsFiltersInput,
  ToeInputsSortInput,
} from "gql/graphql";

const DEBOUNCE_DELAY = 500;
const PROCESSING_ROWS_LIMIT = 5000;

interface IUseToeInputsPaginationOptions {
  variables: GetToeInputsQueryVariables;
  filters: ToeInputsFiltersInput | null;
  sortBy?: ToeInputsSortInput[];
  fetchPolicy?: FetchPolicy;
  nextFetchPolicy?: FetchPolicy;
}

export interface IUseToeInputsPaginationReturn {
  data?: GetToeInputsQuery;
  fetchReportData: () => Promise<void>;
  handleNextPage: () => Promise<void>;
  handleSearch: (search: string) => void;
  loading: boolean;
  refetch: (
    variables?: Partial<GetToeInputsQueryVariables>,
  ) => Promise<ApolloQueryResult<GetToeInputsQuery>>;
}

export function useToeInputsPagination({
  variables,
  filters,
  sortBy = [],
  fetchPolicy = "network-only",
  nextFetchPolicy = "cache-first",
}: IUseToeInputsPaginationOptions): IUseToeInputsPaginationReturn {
  const { data, loading, fetchMore, refetch } = useQuery<GetToeInputsQuery>(
    GET_TOE_INPUTS,
    {
      fetchPolicy,
      nextFetchPolicy,
      variables: {
        ...variables,
        filters,
        first: 150,
        sortBy,
      },
    },
  );

  const handleNextPage = useCallback(async (): Promise<void> => {
    const pageInfo =
      data === undefined
        ? { endCursor: "", hasNextPage: false }
        : {
            endCursor: data.group.toeInputs.pageInfo.endCursor,
            hasNextPage: data.group.toeInputs.pageInfo.hasNextPage,
          };
    if (pageInfo.hasNextPage) {
      await fetchMore({ variables: { after: pageInfo.endCursor } });
    }
  }, [data, fetchMore]);

  const fetchReportData = useCallback(async (): Promise<void> => {
    if (!data) return;

    const initialEdges = data.group.toeInputs.edges;
    const initialEndCursor = data.group.toeInputs.pageInfo.endCursor;

    const fetchAll = async (
      accumulatedEdges: typeof initialEdges,
      endCursor: string,
      count: number,
    ): Promise<typeof accumulatedEdges> => {
      if (count >= PROCESSING_ROWS_LIMIT) return accumulatedEdges;

      const result = await fetchMore({ variables: { after: endCursor } });
      const newEdges = result.data.group.toeInputs.edges;
      const { pageInfo } = result.data.group.toeInputs;

      const updatedEdges = [...accumulatedEdges, ...newEdges];
      const newCount = count + newEdges.length;

      if (!pageInfo.hasNextPage || newCount >= PROCESSING_ROWS_LIMIT) {
        return updatedEdges;
      }

      return fetchAll(updatedEdges, pageInfo.endCursor, newCount);
    };

    await fetchAll(initialEdges, initialEndCursor, initialEdges.length);
  }, [data, fetchMore]);

  const handleSearch = useDebouncedCallback((search: string): void => {
    void refetch({ filters: { search } });
  }, DEBOUNCE_DELAY);

  return {
    data,
    fetchReportData,
    handleNextPage,
    handleSearch,
    loading,
    refetch,
  };
}
